import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../lib/axios';
import Layout from '../../components/layout/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { PenLine, Plus, Search, Loader2, Copy, Trash2, Sparkles, Wand2, FileImage, FileText as FileTextIcon, Lock, Printer, Columns2, Star, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { LetterheadTab } from './LetterheadTab';
import { LetterPrintDialog } from './LetterPrintDialog';
import { ToneCompareDialog } from './ToneCompareDialog';
import { CourseContextFields, coursesAtLocations } from './courseContext';
import { useActionBarConfig } from '../../contexts/ActionBarContext';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';

import { formatDate } from '@/lib/utils';

const fmtDate = (d) => (d ? formatDate(d) || '–' : '–');
const EMPTY_FORM = { module: 'Allgemein', rubrik: '', name: '', subject: '', body: '' };
const RUBRIKEN = [
  'Abmeldung', 'Anmeldung', 'App', 'Aufführung', 'Ausfall', 'Beiträge', 'Dokumente', 'Gebühren',
  'Honorar', 'Jeki', 'Personal', 'Rücklastschrift', 'System', 'Tanz', 'Verträge', 'Werbung',
];

const AI_OCCASIONS = [
  { value: '', label: 'Anlass wählen…' },
  { value: 'mahnung', label: 'Mahnung / Zahlungserinnerung' },
  { value: 'einladung', label: 'Einladung' },
  { value: 'info', label: 'Information' },
  { value: 'absage', label: 'Absage' },
  { value: 'erinnerung', label: 'Erinnerung' },
  { value: 'bestaetigung', label: 'Bestätigung' },
  { value: 'dank', label: 'Dankschreiben' },
];

const AI_TONES = [
  { value: 'freundlich', label: 'Freundlich' },
  { value: 'formell', label: 'Formell' },
  { value: 'herzlich', label: 'Herzlich' },
  { value: 'sachlich', label: 'Sachlich' },
  { value: 'bestimmt', label: 'Bestimmt' },
];

export default function CorrespondencePage() {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [rubrikByModule, setRubrikByModule] = useState({});
  const [collapsedModules, setCollapsedModules] = useState({});
  const [editing, setEditing] = useState(null);
  const [editingSystem, setEditingSystem] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [dupTarget, setDupTarget] = useState(null);
  const [dupName, setDupName] = useState('');
  const [dupSaving, setDupSaving] = useState(false);
  const [printTarget, setPrintTarget] = useState(null);
  const [locations, setLocations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [courseIds, setCourseIds] = useState([]);
  const [coursePreview, setCoursePreview] = useState('');

  useActionBarConfig({
    onNew: () => openEditor(null),
    newLabel: 'Neue Vorlage anlegen',
  }, []);
  const [aiLoading, setAiLoading] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiOccasion, setAiOccasion] = useState('');
  const [aiTone, setAiTone] = useState('freundlich');
  const [showToneCompare, setShowToneCompare] = useState(false);
  const bodyRef = useRef(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await api.get('/correspondence/templates', { params: { search: q || undefined } });
      setTemplates(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  useEffect(() => {
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
    api.get('/correspondence/course-options').then((r) => {
      setLocations(r.data.locations || []);
      setCourses(r.data.courses || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!courseIds.length) { setCoursePreview(''); return undefined; }
    const timer = setTimeout(() => {
      api.post('/correspondence/preview-text', {
        body: form.body, subject: form.subject, course_ids: courseIds,
      }).then((r) => setCoursePreview(r.data.body || '')).catch(() => setCoursePreview(''));
    }, 300);
    return () => clearTimeout(timer);
  }, [courseIds, form.body, form.subject]);

  useEffect(() => {
    if (!courseIds.length || !courses.length || locationIds.length) return;
    setLocationIds([...new Set(
      courses.filter((course) => courseIds.includes(course.id)).map((course) => course.location_id),
    )]);
  }, [courses, courseIds, locationIds.length]);

  const openEditor = (tpl) => {
    setForm(tpl ? { module: tpl.module, rubrik: tpl.rubrik || '', name: tpl.name, subject: tpl.subject || '', body: tpl.body || '' } : EMPTY_FORM);
    setEditingSystem(!!tpl?.system_key);
    setShowAiPrompt(false);
    setAiPrompt('');
    const selectedCourses = tpl?.course_ids || [];
    setCourseIds(selectedCourses);
    setLocationIds([...new Set(
      courses.filter((course) => selectedCourses.includes(course.id)).map((course) => course.location_id),
    )]);
    setCoursePreview('');
    setEditing(tpl ? tpl.id : 'new');
  };

  const chooseLocations = (ids) => {
    setLocationIds(ids);
    const allowed = new Set(coursesAtLocations(courses, ids).map((course) => course.id));
    setCourseIds((current) => current.filter((id) => allowed.has(id)));
  };

  const moduleEntries = Object.entries(templates.reduce((acc, template) => {
    const name = template.module || 'Allgemein';
    (acc[name] = acc[name] || []).push(template);
    return acc;
  }, {})).sort((a, b) => a[0].localeCompare(b[0], 'de'));
  const visibleTemplates = moduleEntries.flatMap(([module, items]) => {
    if (collapsedModules[module]) return [];
    const selected = rubrikByModule[module] || '';
    return selected ? items.filter((template) => (template.rubrik || '') === selected) : items;
  });
  const { getRowProps } = useListKeyboardNav({
    items: visibleTemplates,
    onSelect: openEditor,
    enabled: !editing && !dupTarget && !printTarget,
  });
  const templateIndexById = Object.fromEntries(visibleTemplates.map((t, i) => [t.id, i]));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Bitte eine Bezeichnung angeben'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/correspondence/templates', { ...form, course_ids: courseIds });
        toast.success('Vorlage angelegt');
      } else {
        await api.put(`/correspondence/templates/${editing}`, { ...form, course_ids: courseIds });
        toast.success('Vorlage gespeichert');
      }
      setEditing(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const confirmDuplicate = async () => {
    if (!dupName.trim()) { toast.error('Bitte einen Namen für die Kopie angeben'); return; }
    setDupSaving(true);
    try {
      await api.post(`/correspondence/templates/${dupTarget.id}/duplicate`, { name: dupName.trim() });
      toast.success(`Vorlage „${dupName.trim()}“ angelegt`);
      setDupTarget(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplizieren fehlgeschlagen');
    } finally { setDupSaving(false); }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Vorlage „${tpl.name}“ wirklich löschen?`)) return;
    try {
      await api.delete(`/correspondence/templates/${tpl.id}`);
      toast.success('Vorlage gelöscht');
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const seedDefaults = async () => {
    const r = await api.post('/correspondence/templates/seed-defaults');
    toast.success(`${r.data.created} Standard-Vorlagen angelegt`);
    load(search);
  };

  const toggleFavorite = async (tpl) => {
    try {
      const r = await api.post(`/correspondence/templates/${tpl.id}/favorite`);
      toast.success(r.data.is_favorite
        ? `„${tpl.name}" angepinnt – erscheint beim Versand ganz oben`
        : `„${tpl.name}" nicht mehr angepinnt`);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anpinnen fehlgeschlagen');
    }
  };

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    const body = form.body;
    if (!el) { setForm((f) => ({ ...f, body: f.body + key })); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setForm((f) => ({ ...f, body: body.slice(0, start) + key + body.slice(end) }));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + key.length;
    });
  };

  const aiOptimize = async () => {
    if (!form.body.trim()) { toast.error('Kein Text zum Optimieren vorhanden'); return; }
    setAiLoading('optimize');
    try {
      const r = await api.post('/correspondence/ai/optimize', {
        subject: form.subject, body: form.body, occasion: aiOccasion, tone: aiTone,
      });
      setForm((f) => ({ ...f, subject: r.data.subject || f.subject, body: r.data.body || f.body }));
      toast.success('Text von der KI optimiert – bitte prüfen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Optimierung fehlgeschlagen');
    } finally { setAiLoading(null); }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim() && !aiOccasion) { toast.error('Bitte Anlass wählen oder kurz beschreiben, was für ein Text erstellt werden soll'); return; }
    setAiLoading('generate');
    try {
      const r = await api.post('/correspondence/ai/generate', {
        prompt: aiPrompt, occasion: aiOccasion, tone: aiTone,
      });
      const occLabel = AI_OCCASIONS.find((o) => o.value === aiOccasion)?.label;
      setForm((f) => ({
        ...f,
        subject: r.data.subject || f.subject,
        body: r.data.body || f.body,
        name: f.name || aiPrompt.slice(0, 60) || occLabel || '',
      }));
      setShowAiPrompt(false);
      toast.success('Text von der KI erstellt – bitte prüfen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'KI-Texterstellung fehlgeschlagen');
    } finally { setAiLoading(null); }
  };

  const modules = [...new Set(templates.map((t) => t.module))];
  const rubriken = [...new Set([
    ...RUBRIKEN,
    ...templates.map((t) => t.rubrik).filter(Boolean),
    form.rubrik,
  ].filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de'));

  return (
    <Layout>
      <div className="p-6 space-y-5" data-testid="correspondence-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <PenLine className="h-6 w-6 text-brand-red" /> Korrespondenz
            </h1>
            <p className="text-sm text-slate-500 mt-1">Textvorlagen mit Platzhaltern und Briefpapier verwalten</p>
          </div>
          <Button onClick={() => openEditor(null)} data-testid="correspondence-new-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Neue Vorlage
          </Button>
        </div>

        <Tabs defaultValue="vorlagen">
          <TabsList>
            <TabsTrigger value="vorlagen" data-testid="correspondence-tab-vorlagen"><FileTextIcon className="h-3.5 w-3.5 mr-1" /> Vorlagen</TabsTrigger>
            <TabsTrigger value="briefpapier" data-testid="correspondence-tab-briefpapier"><FileImage className="h-3.5 w-3.5 mr-1" /> Briefpapier</TabsTrigger>
          </TabsList>

          <TabsContent value="vorlagen" className="mt-4">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="relative mb-4 max-w-md">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Schnellsuche: Bezeichnung, Rubrik, Inhalt…" className="pl-9" data-testid="correspondence-search" />
                </div>

                {loading ? (
                  <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>
                ) : templates.length === 0 ? (
                  <div className="py-12 text-center space-y-3">
                    <div className="text-sm text-slate-400">Keine Vorlagen gefunden</div>
                    {!search && (
                      <Button variant="outline" onClick={seedDefaults} data-testid="correspondence-seed-btn">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Standard-Vorlagen anlegen
                      </Button>
                    )}
                  </div>
                ) : (
                  <div data-testid="correspondence-list">
                    {moduleEntries.map(([module, items]) => {
                      const selected = rubrikByModule[module] || '';
                      const shown = selected ? items.filter((template) => (template.rubrik || '') === selected) : items;
                      const categories = [...new Set(items.map((template) => template.rubrik).filter(Boolean))]
                        .sort((a, b) => a.localeCompare(b, 'de'));
                      const collapsed = !!collapsedModules[module];
                      return (
                      <div key={module} className="mb-2">
                        <button
                          type="button"
                          onClick={() => setCollapsedModules((current) => ({ ...current, [module]: !current[module] }))}
                          className="flex w-full items-center gap-1.5 bg-slate-100 rounded px-3 py-1.5 text-left text-xs font-bold uppercase tracking-wider text-slate-600"
                          aria-expanded={!collapsed}
                          data-testid={`correspondence-module-${module}`}
                        >
                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                          {module} ({shown.length})
                        </button>
                        {collapsed ? null : (
                        <>
                        <label className="mt-2 flex w-56 flex-col gap-1 px-3 text-xs font-medium text-slate-500">
                          Kategorie
                          <select
                            value={selected}
                            onChange={(e) => setRubrikByModule((current) => ({ ...current, [module]: e.target.value }))}
                            className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                            data-testid={`correspondence-rubrik-filter-${module}`}
                          >
                            <option value="">Alle Kategorien</option>
                            {categories.map((rubrik) => <option key={rubrik} value={rubrik}>{rubrik}</option>)}
                          </select>
                        </label>
                        {shown.length === 0 ? (
                          <div className="px-3 py-6 text-sm text-slate-400">Keine Vorlagen in dieser Rubrik</div>
                        ) : (
                        <table className="w-full text-sm">
                          <tbody>
                            {shown.map((t) => (
                              <tr
                                key={t.id}
                                className={LIST_ROW_CLASS}
                                data-testid={`correspondence-row-${t.id}`}
                                {...getRowProps(templateIndexById[t.id] ?? 0, t)}
                              >
                                <td className="py-2 px-3 w-36 text-xs text-slate-500">{t.rubrik || '–'}</td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => openEditor(t)} className="font-medium text-slate-900 hover:text-brand-red transition-colors text-left" data-testid={`correspondence-name-${t.id}`}>
                                      {t.name}
                                    </button>
                                    {t.is_favorite && (
                                      <Star className="h-3.5 w-3.5 text-brand-red fill-amber-400 shrink-0" data-testid={`correspondence-favorite-badge-${t.id}`} />
                                    )}
                                    {t.system_key && (
                                      <Badge className="border-0 bg-brand-red/10 text-brand-red text-[10px]" title="Wird automatisch beim jeweiligen Versand verwendet – editierbar, aber nicht löschbar" data-testid={`correspondence-system-badge-${t.id}`}>
                                        System
                                      </Badge>
                                    )}
                                  </div>
                                  {t.subject && <div className="text-xs text-slate-500 truncate max-w-md">{t.subject}</div>}
                                </td>
                                <td className="py-2 px-3 w-40 text-xs text-slate-500 whitespace-nowrap">
                                  {t.last_used_at ? <>zuletzt: {fmtDate(t.last_used_at)}</> : <span className="text-slate-300">nie verwendet</span>}
                                </td>
                                <td className="py-2 px-3 w-40 text-right whitespace-nowrap">
                                  <button
                                    onClick={() => toggleFavorite(t)}
                                    title={t.is_favorite ? 'Angepinnt – erscheint beim Versand ganz oben (Klick zum Lösen)' : 'Als Favorit anpinnen – erscheint beim Versand ganz oben'}
                                    className={`p-1.5 transition-colors ${t.is_favorite ? 'text-brand-red hover:text-brand-red' : 'text-slate-300 hover:text-brand-red'}`}
                                    data-testid={`correspondence-favorite-${t.id}`}
                                  >
                                    <Star className={`h-4 w-4 ${t.is_favorite ? 'fill-amber-400' : ''}`} />
                                  </button>
                                  <button onClick={() => setPrintTarget(t)} title="Brief drucken (PDF mit Briefkopf)" className="p-1.5 text-slate-400 hover:text-brand-red transition-colors" data-testid={`correspondence-print-${t.id}`}><Printer className="h-4 w-4" /></button>
                                  <button onClick={() => { setDupTarget(t); setDupName(`${t.name} (Kopie)`); }} title="Vorlage duplizieren" className="p-1.5 text-slate-400 hover:text-brand-red transition-colors" data-testid={`correspondence-duplicate-${t.id}`}><Copy className="h-4 w-4" /></button>
                                  {t.system_key ? (
                                    <span className="p-1.5 inline-block text-slate-200" title="System-Vorlage – kann nicht gelöscht werden" data-testid={`correspondence-system-lock-${t.id}`}><Lock className="h-4 w-4" /></span>
                                  ) : (
                                    <button onClick={() => remove(t)} title="Löschen" className="p-1.5 text-slate-400 hover:text-rose-600 transition-colors" data-testid={`correspondence-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        )}
                        </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="briefpapier" className="mt-4">
            <LetterheadTab />
          </TabsContent>
        </Tabs>

        {/* Editor */}
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="correspondence-editor-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editing === 'new' ? 'Neue Vorlage' : 'Vorlage bearbeiten'}
                {editingSystem && <Badge className="border-0 bg-brand-red/10 text-brand-red">System-Vorlage</Badge>}
              </DialogTitle>
              <DialogDescription>
                {editingSystem
                  ? 'Diese Vorlage wird automatisch beim jeweiligen Versand verwendet. Änderungen wirken sofort.'
                  : 'Textvorlage mit Platzhaltern – wird beim Versand automatisch personalisiert.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Bezeichnung *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="z. B. Abmeldebestätigung" className="mt-1" data-testid="correspondence-form-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">Modul</label>
                  <Input value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} list="correspondence-modules" className="mt-1" data-testid="correspondence-form-module" />
                  <datalist id="correspondence-modules">
                    {['Unterrichtsverwaltung', 'Adressverwaltung', 'Honorarverwaltung', 'Kontenverwaltung', 'Personalverwaltung', 'Inventarverwaltung (Verträge)', 'Abrechnung', 'Allgemein', ...modules].filter((m, i, a) => a.indexOf(m) === i).map((m) => <option key={m} value={m} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Rubrik</label>
                  <select
                    value={form.rubrik}
                    onChange={(e) => setForm({ ...form, rubrik: e.target.value })}
                    className="mt-1 flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"
                    data-testid="correspondence-form-rubrik"
                  >
                    <option value="">Rubrik wählen…</option>
                    {rubriken.map((rubrik) => <option key={rubrik} value={rubrik}>{rubrik}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Betreff (E-Mail)</label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1" data-testid="correspondence-form-subject" />
              </div>

              {/* KI-Textwerkstatt */}
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand-red/10 bg-brand-red/5 px-3 py-2">
                <Sparkles className="h-4 w-4 text-brand-red" />
                <span className="text-xs font-medium text-brand-red">KI-Textwerkstatt:</span>
                <select
                  value={aiOccasion}
                  onChange={(e) => setAiOccasion(e.target.value)}
                  className="h-7 rounded-md border border-brand-red/20 bg-white px-2 text-xs text-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red/40"
                  title="Anlass des Schreibens"
                  data-testid="correspondence-ai-occasion"
                >
                  {AI_OCCASIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <select
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}
                  className="h-7 rounded-md border border-brand-red/20 bg-white px-2 text-xs text-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red/40"
                  title="Tonalität des Textes"
                  data-testid="correspondence-ai-tone"
                >
                  {AI_TONES.map((t) => <option key={t.value} value={t.value}>Ton: {t.label}</option>)}
                </select>
                <Button variant="outline" size="sm" className="h-7 text-xs border-brand-red/20 text-brand-red hover:bg-brand-red/10" onClick={aiOptimize} disabled={!!aiLoading} data-testid="correspondence-ai-optimize-btn">
                  {aiLoading === 'optimize' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
                  KI-Textoptimierung
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs border-brand-red/20 text-brand-red hover:bg-brand-red/10" onClick={() => setShowAiPrompt((v) => !v)} disabled={!!aiLoading} data-testid="correspondence-ai-generate-toggle">
                  <Sparkles className="h-3.5 w-3.5 mr-1" /> KI-Texterstellung
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs border-brand-red/20 text-brand-red hover:bg-brand-red/10" onClick={() => setShowToneCompare(true)} disabled={!!aiLoading} data-testid="correspondence-ai-tone-compare-btn">
                  <Columns2 className="h-3.5 w-3.5 mr-1" /> Ton-Vorschau
                </Button>
              </div>
              {showAiPrompt && (
                <div className="flex gap-2" data-testid="correspondence-ai-prompt-row">
                  <Input
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aiGenerate(); } }}
                    placeholder={aiOccasion
                      ? `Optionale Details zum Anlass „${AI_OCCASIONS.find((o) => o.value === aiOccasion)?.label}" – oder leer lassen für Standardtext`
                      : 'Was soll die KI schreiben? z. B. „Absage einer Schnupperstunde wegen Krankheit der Lehrkraft" – oder oben einen Anlass wählen'}
                    className="text-sm"
                    data-testid="correspondence-ai-prompt"
                  />
                  <Button size="sm" onClick={aiGenerate} disabled={!!aiLoading} data-testid="correspondence-ai-generate-btn">
                    {aiLoading === 'generate' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Erstellen'}
                  </Button>
                </div>
              )}

              {!/honorar/i.test(`${form.module || ''} ${form.rubrik || ''}`) && (
              <div className="rounded-md border border-slate-200 px-3 py-2 space-y-2" data-testid="correspondence-course-block">
                <p className="text-xs text-slate-500">
                  Ort und Kurs im Pulldown wählen. Darunter steht die Übersicht aller gewählten Orte und Kurse. Der Grundtext bleibt. Beim Versand gilt je Empfänger der Kurs aus dieser Auswahl, in dem das Kind ist.
                </p>
                <CourseContextFields
                  locations={locations}
                  courses={courses}
                  locationIds={locationIds}
                  courseIds={courseIds}
                  onLocations={chooseLocations}
                  onCourses={setCourseIds}
                />
                {courseIds.length > 1 && (
                  <p className="text-[11px] text-slate-400">Die Vorschau zeigt den ersten gewählten Kurs. Beim Versand gilt je Empfänger der passende Kurs aus der Auswahl.</p>
                )}
                {coursePreview && (
                  <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs text-slate-700" data-testid="correspondence-course-preview">{coursePreview}</pre>
                )}
              </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-500">Text</label>
                <Textarea ref={bodyRef} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={12} className="mt-1 font-mono text-xs" data-testid="correspondence-form-body" />
                <div className="mt-2 max-h-40 overflow-y-auto space-y-2" data-testid="correspondence-placeholder-groups">
                  {Object.entries(placeholders.reduce((groups, item) => {
                    const name = item.group || 'Allgemein';
                    return { ...groups, [name]: [...(groups[name] || []), item] };
                  }, {})).map(([group, items]) => (
                    <div key={group}>
                      <div className="text-[10px] uppercase tracking-wide text-slate-400">{group}</div>
                      <div className="flex flex-wrap gap-1">
                        {items.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => insertPlaceholder(p.key)}
                            title={p.label}
                            className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors"
                            data-testid={`correspondence-placeholder-${p.key.replace(/[<>.]/g, '')}`}
                          >
                            {p.key}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} data-testid="correspondence-form-cancel">Abbrechen</Button>
                <Button onClick={save} disabled={saving} data-testid="correspondence-form-save">
                  {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Brief drucken */}
        <LetterPrintDialog template={printTarget} onClose={() => setPrintTarget(null)} />

        {/* Ton-Vorschau (A/B-Vergleich) */}
        <ToneCompareDialog
          open={showToneCompare}
          onClose={() => setShowToneCompare(false)}
          source={{ subject: form.subject, body: form.body, prompt: aiPrompt, occasion: aiOccasion }}
          defaultTone={aiTone}
          onAdopt={(v) => setForm((f) => ({
            ...f,
            subject: v.subject || f.subject,
            body: v.body || f.body,
            name: f.name || aiPrompt.slice(0, 60) || (aiOccasion ? AI_OCCASIONS.find((o) => o.value === aiOccasion)?.label : '') || '',
          }))}
        />

        {/* Duplizieren mit Namensabfrage */}
        <Dialog open={!!dupTarget} onOpenChange={(o) => !o && setDupTarget(null)}>
          <DialogContent className="max-w-md" data-testid="correspondence-duplicate-dialog">
            <DialogHeader>
              <DialogTitle>Vorlage duplizieren</DialogTitle>
              <DialogDescription>
                „{dupTarget?.name}“ wird kopiert. Bitte einen Namen für die neue Vorlage vergeben.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Name der neuen Vorlage *</label>
                <Input
                  value={dupName}
                  onChange={(e) => setDupName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmDuplicate(); } }}
                  autoFocus
                  className="mt-1"
                  data-testid="correspondence-duplicate-name"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDupTarget(null)} data-testid="correspondence-duplicate-cancel">Abbrechen</Button>
                <Button onClick={confirmDuplicate} disabled={dupSaving} data-testid="correspondence-duplicate-confirm">
                  {dupSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Copy className="h-4 w-4 mr-1.5" />} Duplizieren
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
